import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import DramaCard from "@/components/feature/DramaCard";
import FilterPanel from "@/components/feature/FilterPanel";
import { dramas } from "@/mocks/dramas";
import { useDramaFilter, sortLabels, type SortKey } from "@/hooks/useDramaFilter";
import { usePageMeta } from "@/hooks/usePageMeta";

const hotKeywords = ["长夜将明", "悬疑", "古装", "陆行舟", "沈砚", "治愈"];
const PAGE_SIZE = 9;

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();
  const [input, setInput] = useState(keyword);
  const [filterOpen, setFilterOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const baseResults = useMemo(() => {
    if (!keyword) return dramas;
    const lower = keyword.toLowerCase();
    return dramas.filter((drama) => {
      const pool = [
        drama.title,
        drama.director,
        drama.region,
        drama.tagline,
        drama.synopsis,
        drama.analysis,
        ...drama.cast,
        ...drama.types,
      ]
        .join(" ")
        .toLowerCase();
      return pool.includes(lower);
    });
  }, [keyword]);

  const filter = useDramaFilter(baseResults);
  const {
    selectedTypes,
    selectedYears,
    selectedRegions,
    sortKey,
    setSortKey,
    toggleType,
    toggleYear,
    toggleRegion,
    reset,
    results,
    activeCount,
  } = filter;

  usePageMeta({
    title: keyword
      ? `“${keyword}”的搜索结果 - 波多野结衣家庭教师`
      : "搜索剧集 - 波多野结衣家庭教师",
    description: keyword
      ? `与“${keyword}”相关的电视剧搜索结果，可按类型、年份与地区继续筛选，查看剧情介绍与角色分析。`
      : "输入剧名、演员或类型关键词，快速检索站内电视剧内容，并按类型、年份、地区筛选结果。",
    keywords: `${keyword || "剧集搜索"},电视剧搜索,剧情介绍,角色分析,波多野结衣家庭教师`,
  });

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [keyword, selectedTypes, selectedYears, selectedRegions, sortKey]);

  useEffect(() => {
    document.body.style.overflow = filterOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [filterOpen]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = input.trim();
    setSearchParams(query ? { q: query } : {});
    setVisibleCount(PAGE_SIZE);
  };

  const runQuickSearch = (value: string) => {
    setInput(value);
    setSearchParams({ q: value });
  };

  const filterPanel = (
    <FilterPanel
      selectedTypes={selectedTypes}
      selectedYears={selectedYears}
      selectedRegions={selectedRegions}
      onToggleType={toggleType}
      onToggleYear={toggleYear}
      onToggleRegion={toggleRegion}
      onReset={reset}
    />
  );

  const visibleResults = results.slice(0, visibleCount);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="pt-[72px]">
        <section className="border-b border-background-200/70 bg-background-100/50">
          <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-1.5 font-label text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-accent-500">首页</Link>
              <i className="ri-arrow-right-s-line text-sm opacity-60"></i>
              <span className="text-foreground-800">搜索</span>
            </nav>
            <h1 className="mt-4 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">
              搜索剧集
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              支持剧名、演员、导演与类型关键词匹配，结果可在当前页继续筛选与排序。
            </p>

            <form onSubmit={submitSearch} className="mt-5 max-w-2xl">
              <div className="relative flex items-center">
                <i className="ri-search-line pointer-events-none absolute left-4 text-base text-foreground-500"></i>
                <input
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  placeholder="输入剧名、演员或类型，例如：长夜将明"
                  aria-label="搜索关键词"
                  className="h-12 w-full rounded-md border border-background-300/70 bg-background-50 pl-11 pr-28 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                />
                <button
                  type="submit"
                  className="absolute right-1.5 flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded bg-primary-500 px-4 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-search-line text-sm"></i>
                  搜索
                </button>
              </div>
            </form>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="font-label text-[11px] tracking-[0.18em] text-foreground-500">热门检索</span>
              {hotKeywords.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => runQuickSearch(item)}
                  className="cursor-pointer whitespace-nowrap rounded-full border border-background-300/60 px-2.5 py-1 font-label text-[11px] text-foreground-700 transition-colors hover:border-accent-500/60 hover:text-accent-600"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
          {keyword ? (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className="text-sm text-foreground-600">
                当前检索词
                <span className="mx-2 rounded bg-primary-500/12 px-2 py-0.5 font-label text-primary-500">
                  {keyword}
                </span>
                共找到 <span className="text-foreground-950">{baseResults.length}</span> 部相关剧集
              </p>
              <Link
                to="/search"
                className="cursor-pointer font-label text-[11px] text-foreground-500 underline underline-offset-2 hover:text-primary-500"
              >
                清除关键词
              </Link>
            </div>
          ) : (
            <p className="text-sm text-foreground-600">
              尚未输入关键词，下面是站内全部剧集（{dramas.length} 部），你也可以先筛选类型再浏览。
            </p>
          )}

          <div className="mt-5 grid gap-6 lg:grid-cols-[276px_1fr] lg:gap-8">
            <aside className="hidden lg:block">
              <div className="sticky top-24">{filterPanel}</div>
            </aside>

            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200/70 bg-background-100/70 px-4 py-3">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setFilterOpen(true)}
                    className="flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300/60 px-3 py-1.5 font-label text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500 lg:hidden"
                  >
                    <i className="ri-filter-3-line text-sm"></i>
                    筛选
                    {activeCount > 0 && (
                      <span className="rounded-full bg-primary-500 px-1.5 text-[10px] text-background-50">
                        {activeCount}
                      </span>
                    )}
                  </button>
                  <p className="font-label text-xs text-foreground-600">
                    筛选后剩余 <span className="text-foreground-950">{results.length}</span> 部
                  </p>
                </div>
                <div className="relative">
                  <select
                    aria-label="排序方式"
                    value={sortKey}
                    onChange={(event) => setSortKey(event.target.value as SortKey)}
                    className="h-9 cursor-pointer appearance-none rounded-md border border-background-300/60 bg-background-50 pl-3 pr-9 font-label text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
                  >
                    {(Object.keys(sortLabels) as SortKey[]).map((key) => (
                      <option key={key} value={key}>
                        {sortLabels[key]}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
                </div>
              </div>

              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={reset}
                  className="mt-3 cursor-pointer font-label text-[11px] text-foreground-500 underline underline-offset-2 hover:text-primary-500"
                >
                  清空筛选条件（已选 {activeCount} 项）
                </button>
              )}

              {results.length > 0 ? (
                <>
                  <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-7 md:grid-cols-3 md:gap-x-5">
                    {visibleResults.map((drama) => (
                      <DramaCard key={drama.id} drama={drama} />
                    ))}
                  </div>
                  {visibleResults.length < results.length && (
                    <div className="mt-8 flex justify-center">
                      <button
                        type="button"
                        onClick={() => setVisibleCount((prev) => prev + 6)}
                        className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 bg-background-100 px-5 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-500"
                      >
                        <i className="ri-add-line text-sm"></i>
                        加载更多（剩余 {results.length - visibleResults.length} 部）
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-6 rounded-lg border border-dashed border-background-300/70 bg-background-100/50 px-6 py-14 text-center">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200/70 text-foreground-500">
                    <i className="ri-search-eye-line text-2xl"></i>
                  </span>
                  <p className="mt-4 font-heading text-lg text-foreground-900">
                    {keyword ? `没有找到与“${keyword}”相关的剧集` : "暂无符合条件的剧集"}
                  </p>
                  <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                    可以试试更换关键词，或清空筛选条件。也可以直接浏览分类页，按类型找到想看的剧。
                  </p>
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={reset}
                      className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
                    >
                      重置筛选条件
                    </button>
                    <Link
                      to="/category"
                      className="cursor-pointer whitespace-nowrap rounded-md border border-background-300/60 px-4 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:border-accent-500/60 hover:text-accent-600"
                    >
                      去分类页逛逛
                    </Link>
                  </div>

                  <div className="mt-8 border-t border-background-200/70 pt-6 text-left">
                    <p className="font-label text-[11px] tracking-[0.2em] text-accent-600">也许你会喜欢</p>
                    <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-6 md:grid-cols-3">
                      {dramas.slice(0, 3).map((drama) => (
                        <DramaCard key={drama.id} drama={drama} hideSummary />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />

      {filterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选面板"
            onClick={() => setFilterOpen(false)}
            className="absolute inset-0 cursor-pointer bg-background-50/80 backdrop-blur-sm"
          ></button>
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl border-t border-background-300/60 bg-background-50 p-4 pb-8">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-heading text-base text-foreground-950">筛选搜索结果</p>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                aria-label="关闭"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-300/60 text-foreground-700"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            </div>
            {filterPanel}
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-4 w-full cursor-pointer rounded-md bg-primary-500 py-3 font-label text-sm font-medium text-background-50"
            >
              查看 {results.length} 部剧集
            </button>
          </div>
        </div>
      )}
    </div>
  );
}